import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFieldSalesCreditNotes } from '../hooks/useFieldSalesDashboard.js';
import { formatValueAuto, formatIndianInt, FS_HEAD_SX, FS_CELL_SX } from './fieldSalesFormat.js';
import FieldSalesPager from './FieldSalesPager.jsx';
import { usePagedRows } from './fieldSalesPaging.js';

const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';
const NO_DATA_TOOLTIP = 'No data source yet';

/** ₹ Cr/L (lakh input), em dash for non-positive. */
const money = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

// The three leakage types: wire value + label + totals key + faint card tint.
// Reference card colours: Expiry red, Sales Return light-red, Rate Diff amber.
const TYPES = [
  { type: 'expiry', label: 'Expiry', key: 'expiry', bg: 'rgba(220,38,38,.10)', color: '#DC2626', border: 'rgba(220,38,38,.3)' },
  { type: 'sales_return', label: 'Sales Return', key: 'sales_return', bg: 'rgba(248,113,113,.14)', color: '#E11D48', border: 'rgba(225,29,72,.3)' },
  { type: 'rate_diff', label: 'Rate Diff', key: 'rate_diff', bg: 'rgba(245,158,11,.14)', color: '#B45309', border: 'rgba(245,158,11,.35)' },
];

const HEAD_SX = FS_HEAD_SX;
const CELL_SX = FS_CELL_SX;

/** Greyed Zone cell: the backend returns the state there, so Zone has no source. */
function ZoneCell() {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box
        component="span"
        aria-disabled="true"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.5, cursor: 'not-allowed' }}
      >
        —
        <Box
          component="span"
          sx={{
            px: '6px',
            py: '1px',
            borderRadius: '999px',
            bgcolor: '#eef1f5',
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: '9px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.3px',
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Box>
      </Box>
    </Tooltip>
  );
}

/** Type-switch pill (reference #cnSwitcher). */
function TypePill({ label, active, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        px: '15px',
        py: '6px',
        borderRadius: '999px',
        cursor: 'pointer',
        fontSize: '12.5px',
        fontWeight: 600,
        font: 'inherit',
        border: '1.5px solid',
        transition: 'all .12s',
        ...(active
          ? {
              background: 'linear-gradient(135deg,#2E7CF6,#1657c8)',
              color: '#fff',
              borderColor: '#1657c8',
              boxShadow: '0 3px 9px rgba(46,124,246,.45)',
            }
          : {
              bgcolor: '#eef4ff',
              color: '#2554a8',
              borderColor: '#bcd5fb',
              '&:hover': { bgcolor: '#dbe8ff', borderColor: '#8fbaf6' },
            }),
      }}
    >
      {label}
    </Box>
  );
}

/** A faint totals card for one leakage type. */
function TotalCard({ meta, cn, pct }) {
  return (
    <Box sx={{ bgcolor: meta.bg, border: `1px solid ${meta.border}`, borderRadius: '14px', p: '14px 16px' }}>
      <Typography sx={{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 700, color: meta.color }}>
        {meta.label}
      </Typography>
      <Typography sx={{ fontSize: '22px', fontWeight: 800, color: NAVY, mt: '3px', lineHeight: 1.12 }}>
        {money(cn)}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: MUTED, mt: '3px' }}>
        {Number.isFinite(Number(pct)) ? `${Number(pct).toFixed(2)}% of primary sales` : '—'}
      </Typography>
    </Box>
  );
}

/**
 * Credit Notes Analysis tab (reference "Credit Notes Analysis — Sales Leakage").
 * Subheading "Sales Leakage". A type switch (Expiry / Sales Return / Rate Diff)
 * sends type=expiry|sales_return|rate_diff. Three faint totals cards from the
 * API `totals`, then the table (HQ, Division, Zone, Primary Sales, CN, CN to
 * Sales %) with a Total row. Zone is a greyed "—". Values in Lakhs → ₹ Cr/L.
 */
export default function FieldSalesCreditNotesTab() {
  const [type, setType] = useState('expiry');
  const query = useFieldSalesCreditNotes(type);
  const data = query.data;
  const totals = data?.totals ?? {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const loading = query.isLoading || query.isFetching;

  const multiDiv = new Set(rows.map((r) => r.division)).size > 1;
  const selected = TYPES.find((t) => t.type === type) ?? TYPES[0];

  // Totals for the table footer (over ALL rows, not just the page).
  const tPri = rows.reduce((s, r) => s + (Number(r.primary) || 0), 0);
  const tCn = rows.reduce((s, r) => s + (Number(r.cn) || 0), 0);
  const tPct = tPri > 0 ? (tCn / tPri) * 100 : NaN;

  const grand = totals.grand_total ?? {};

  // Client-side pagination (50/page); resets when the type or row count changes.
  const paged = usePagedRows(rows, { resetKey: `${type}:${rows.length}` });

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '16px 18px',
        mt: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', mb: '2px' }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY }}>💧 Credit Notes Analysis</Typography>
        <Box sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
          {TYPES.map((t) => (
            <TypePill key={t.type} label={t.label} active={type === t.type} onClick={() => setType(t.type)} />
          ))}
        </Box>
      </Box>
      <Typography sx={{ fontSize: '13px', color: MUTED, mb: '12px' }}>Sales Leakage</Typography>

      {query.isError ? (
        <Alert severity="error">Failed to load credit notes.</Alert>
      ) : (
        <>
          {/* Three faint totals cards. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' },
              gap: '12px',
              mb: '12px',
            }}
          >
            {TYPES.map((t) => (
              <TotalCard key={t.type} meta={t} cn={totals[t.key]?.cn} pct={totals[t.key]?.pct} />
            ))}
          </Box>

          {/* Note line. */}
          <Typography sx={{ fontSize: '12px', color: MUTED, mb: '10px' }}>
            Total credit notes (Expiry + Sales Return + Rate Diff) = <b>{money(grand.cn)}</b>
            {Number.isFinite(Number(grand.pct)) ? (
              <>
                {' · '}
                <b>{Number(grand.pct).toFixed(2)}%</b> of primary sales
              </>
            ) : null}
            {Number.isFinite(Number(totals.primary)) ? ` (${money(totals.primary)})` : ''}. Higher % = more leakage.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              No {selected.label} credit notes for the current filters.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 480, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_SX}>#</TableCell>
                    <TableCell sx={HEAD_SX}>HQ</TableCell>
                    {multiDiv ? <TableCell sx={HEAD_SX}>Division</TableCell> : null}
                    <TableCell sx={HEAD_SX}>Zone</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Primary Sales</TableCell>
                    <TableCell sx={HEAD_SX} align="right">{selected.label}</TableCell>
                    <TableCell sx={HEAD_SX} align="right">CN to Sales %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.pageRows.map((r, i) => (
                    <TableRow key={`${r.hq}-${r.division}-${i}`} hover>
                      <TableCell sx={CELL_SX}>{paged.offset + i + 1}</TableCell>
                      <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{r.hq ?? '—'}</TableCell>
                      {multiDiv ? <TableCell sx={CELL_SX}>{r.division ?? '—'}</TableCell> : null}
                      <TableCell sx={CELL_SX}><ZoneCell /></TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.primary)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.cn)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        {Number.isFinite(Number(r.pct)) ? `${Number(r.pct).toFixed(1)}%` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { bgcolor: '#f8fafc', fontWeight: 700, borderTop: `2px solid ${BORDER}` } }}>
                    <TableCell sx={CELL_SX} colSpan={multiDiv ? 4 : 3}>
                      Total · {formatIndianInt(rows.length)} HQ{rows.length === 1 ? '' : 's'}
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(tPri)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(tCn)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      {Number.isFinite(tPct) ? `${tPct.toFixed(1)}%` : '—'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && rows.length > 0 ? (
            <FieldSalesPager page={paged.page} pageCount={paged.pageCount} total={paged.total} onChange={paged.setPage} unit="HQs" />
          ) : null}
        </>
      )}
    </Box>
  );
}
